import {
  BillGroupErrorStatusEnum,
  IBillGroupResp,
  InvoiceCreationEnum,
} from '@monetize/types/app';
import { parseDateAsUtc, toDateShort } from '@monetize/utils/core';
import { addDays } from 'date-fns/addDays';
import { useMemo, useState } from 'react';
import { MdArrowDropDown } from 'react-icons/md';
import { useGetById } from '../../../api/queryUtils';
import {
  MBox,
  MDropdownActionItem,
  MDropdownActions,
  MFlex,
  MIcon,
  MText,
} from '../../../components/Monetize';
import { usePastInvoice } from '../BillGroups/hooks/usePastInvoice';
import { BillGroupInvoicePreviewDrawer } from '../Overview/PreviewBillGroupInvoicePdfDrawer';

const renderTrigger = () => (
  <MFlex
    align="center"
    gap={0.5}
    h={8}
    pl={2}
    pr={1}
    color="tIndigo.base"
    fontSize="sm"
    fontWeight="medium"
  >
    Invoicing
    <MIcon as={MdArrowDropDown} boxSize={5} />
  </MFlex>
);

interface BillGroupInvoicingMenuProps {
  accountId: string;
  billGroupId: string;
  /** Keeps the trigger's data-testid unique when several menus share a screen */
  testIdSuffix: string;
}

/**
 * Preview the next invoice for a bill group, and generate one that is past due.
 * The full bill group is fetched the first time the menu opens.
 */
export const BillGroupInvoicingMenu = ({
  accountId,
  billGroupId,
  testIdSuffix,
}: BillGroupInvoicingMenuProps) => {
  const [hasOpened, setHasOpened] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const {
    data: billGroup,
    isError,
    refetch,
  } = useGetById<IBillGroupResp>('billGroups', billGroupId, {
    enabled: hasOpened,
  });

  const invoiceDelay =
    billGroup?.invoiceCreation === InvoiceCreationEnum.AUTOMATIC
      ? billGroup.invoiceDelay
      : 0;
  const {
    canUpdate,
    hasPastInvoices,
    isLoading: isGenerating,
    generatePreviousInvoice,
  } = usePastInvoice({
    accountId,
    billGroupId,
    billGroupStatus: billGroup?.status ?? '',
    nextInvoiceDate: billGroup?.nextInvoiceDate ?? '',
    invoiceDelay,
  });

  const popoverProps = useMemo(
    () => ({
      onOpenChange: ({ open }: { open: boolean }) => {
        if (open) {
          setHasOpened(true);
        }
      },
    }),
    [],
  );
  const menuButtonProps = useMemo(
    () => ({
      'aria-label': 'Invoicing',
      'data-testid': `lineage-invoicing-btn-${testIdSuffix}`,
    }),
    [testIdSuffix],
  );

  const renderItems = () => {
    if (isError && !billGroup) {
      return (
        <MDropdownActionItem
          id="retry-invoicing"
          closeOnSelect={false}
          onClick={() => refetch()}
        >
          Couldn't load invoice details. Try again
        </MDropdownActionItem>
      );
    }
    if (!billGroup) {
      return <MDropdownActionItem isDisabled>Loading…</MDropdownActionItem>;
    }
    const hasTaxError =
      billGroup.errorStatus === BillGroupErrorStatusEnum.TAX_CALCULATION_ERROR;

    return (
      <>
        {billGroup.hasPreviewableInvoices ? (
          <MDropdownActionItem
            id="preview-invoice"
            onClick={() => setIsPreviewOpen(true)}
          >
            Preview Invoice
          </MDropdownActionItem>
        ) : (
          <MDropdownActionItem isDisabled>
            No upcoming invoice to preview
          </MDropdownActionItem>
        )}
        {canUpdate && hasPastInvoices && billGroup.nextInvoiceDate && (
          <MDropdownActionItem
            id="generate-invoice"
            isDisabled={hasTaxError || isGenerating}
            onClick={() => generatePreviousInvoice({ accountId, billGroupId })}
          >
            {hasTaxError ? (
              'Tax calculation error on the most recent invoice'
            ) : (
              <MBox>
                <MText color="inherit">Generate Pending Invoice</MText>
                <MText fontSize="xs" color="fg.muted">
                  Next Invoice Date is{' '}
                  {toDateShort(
                    addDays(
                      parseDateAsUtc(billGroup.nextInvoiceDate),
                      invoiceDelay,
                    ),
                  )}
                </MText>
              </MBox>
            )}
          </MDropdownActionItem>
        )}
      </>
    );
  };

  return (
    <>
      <MDropdownActions
        renderTrigger={renderTrigger}
        popoverProps={popoverProps}
        menuButtonProps={menuButtonProps}
      >
        {renderItems}
      </MDropdownActions>
      {isPreviewOpen && (
        <BillGroupInvoicePreviewDrawer
          accountId={accountId}
          billGroupId={billGroupId}
          onClose={() => setIsPreviewOpen(false)}
        />
      )}
    </>
  );
};
